import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Safe JSON parse function that throws on malformed JSON
 * and logs errors to server-side dev logging system with database tracking
 * 
 * @param json - JSON string to parse
 * @param context - Optional context for debugging (e.g., function name, file location)
 * @returns Parsed JSON object
 * @throws SyntaxError if JSON is malformed
 */
export function safeJsonParse<T = any>(json: string, context?: string): T {
  try {
    if (!json || typeof json !== 'string') {
      throw new TypeError(`Invalid JSON input: expected string, got ${typeof json}`)
    }
    return JSON.parse(json) as T
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    const fullContext = `[JSON Parse Error]${context ? ` ${context}` : ''}`
    
    // Log to console for immediate visibility
    console.error(`${fullContext}: ${errorMessage}`, {
      input: json?.substring(0, 100),
      fullInput: json,
      stack: error instanceof Error ? error.stack : undefined
    })
    
    // Send to server-side dev logging system (async, non-blocking)
    logToServerDevCenter({
      logType: 'json_parse_error' as const,
      severity: 'error' as const,
      message: errorMessage,
      context: fullContext,
      inputData: json?.substring(0, 500),
      stackTrace: error instanceof Error ? error.stack : String(error),
      source: 'safeJsonParse',
      metadata: {
        fullInputLength: json?.length,
        inputPreview: json?.substring(0, 100)
      }
    })
    
    // Re-throw the error to prevent silent failures
    throw error
  }
}

/**
 * Server-side development logging center
 * - Forwards logs to external endpoint with authentication (if configured)
 * - Stores logs in database for retrieval and analysis
 * - Feature-flagged for jp@naible.ai
 */
async function logToServerDevCenter(logData: {
  logType: 'json_parse_error' | 'api_error' | 'validation_error' | 'runtime_error' | 'custom'
  severity: 'debug' | 'info' | 'warning' | 'error' | 'critical'
  message: string
  context?: string
  inputData?: string
  stackTrace?: string
  source?: string
  metadata?: Record<string, any>
}): Promise<void> {
  // Feature flag check for jp@naible.ai
  const devEmail = 'jp@naible.ai'
  
  try {
    // Try to get current user from localStorage/session
    const currentUserEmail = localStorage?.getItem('devUserEmail') || sessionStorage?.getItem('userEmail')
    
    if (currentUserEmail !== devEmail) {
      // Not a dev user - skip logging
      return
    }
    
    // Import dev log service dynamically to avoid circular dependencies
    const { logDevEvent } = await import('../services/devLogService')
    
    // Log to server (database + optional forwarding)
    await logDevEvent({
      ...logData,
      userEmail: devEmail,
      timestamp: new Date().toISOString()
    })
    
    // Also keep local storage for dev tools access
    try {
      const storedLogs = sessionStorage?.getItem('devLogs') || '[]'
      const logs = storedLogs ? JSON.parse(storedLogs) : []
      logs.push({ ...logData, userEmail: devEmail, timestamp: new Date().toISOString() })
      sessionStorage?.setItem('devLogs', JSON.stringify(logs.slice(-100)))
    } catch (e) {
      // Silently fail storage updates - corrupted data may cause parse errors
      console.debug('[DevCenter] Failed to store in sessionStorage:', e)
    }
    
    // Log to console with special formatting
    console.debug('[DEV-CENTER-SERVER]', logData)
  } catch (e) {
    // Silently fail - don't break on logging errors
    console.error('[DEV-CENTER] Failed to log to server:', e)
  }
}

/**
 * Initialize dev user email in session (called on app initialization)
 * This allows the logging center to know if current user is jp@naible.ai
 */
export function initializeDevLogging(userEmail?: string): void {
  if (userEmail === 'jp@naible.ai') {
    sessionStorage?.setItem('userEmail', userEmail)
    localStorage?.setItem('devUserEmail', userEmail)
  }
}

/**
 * Generic dev logging function for custom events
 * Can be used anywhere in the app to log dev events
 */
export async function logDevEvent(
  message: string,
  options?: {
    logType?: 'json_parse_error' | 'api_error' | 'validation_error' | 'runtime_error' | 'custom'
    severity?: 'debug' | 'info' | 'warning' | 'error' | 'critical'
    context?: string
    inputData?: string
    stackTrace?: string
    source?: string
    metadata?: Record<string, any>
  }
): Promise<void> {
  const devEmail = 'jp@naible.ai'
  const currentUserEmail = localStorage?.getItem('devUserEmail') || sessionStorage?.getItem('userEmail')
  
  if (currentUserEmail !== devEmail) {
    return // Only log for dev users
  }
  
  try {
    const { logDevEvent: serverLogDevEvent } = await import('../services/devLogService')
    
    await serverLogDevEvent({
      logType: options?.logType || 'custom',
      severity: options?.severity || 'info',
      message,
      context: options?.context,
      inputData: options?.inputData,
      stackTrace: options?.stackTrace,
      source: options?.source,
      metadata: options?.metadata,
      userEmail: devEmail
    })
  } catch (e) {
    console.error('[DEV-CENTER] Failed to log custom event:', e)
  }
}
